import React, { useImperativeHandle, forwardRef, useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import kycService from '../../services/onboarding/kycService';
import { useOnboarding } from './OnboardingContext';
import './Onboarding.css';
import { useIsMobile } from 'lib/utils';

const validationSchema = Yup.object().shape({
  msmeNumber: Yup.string()
    .nullable()
    .notRequired()
    .matches(
      /^[A-Z]{2}-\d{2}-\d{7}$/,
      'Please enter a valid MSME number (e.g., TS-99-9999999)'
    ),
});

const MSMERegistration = forwardRef(({ title = 'MSME Registration', onComplete }, ref) => {
  const isMobile = useIsMobile();
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [msmeInfo, setMsmeInfo] = useState(null);
  const [fullMsmeInfo, setFullMsmeInfo] = useState(null);
  const [validatingField, setValidatingField] = useState(null);
  const {
    updateSection,
    markStepComplete,
    markStepIncomplete,
    getStepData
  } = useOnboarding();

  // Get existing data for this step
  const existingData = getStepData(3); // Step 3 is msme

  // Initialize state from existing data
  useEffect(() => {
    if (existingData.verifiedInfo) {
      setMsmeInfo(existingData.verifiedInfo);
    }
    if (existingData.fullVerifiedInfo) {
      setFullMsmeInfo(existingData.fullVerifiedInfo);
    }
  }, [existingData]);

  // Function to convert only letters to uppercase while preserving numbers and special characters
  const convertLettersToUppercase = (value) => {
    return value.replace(/[a-zA-Z]/g, (letter) => letter.toUpperCase());
  };

  const handleMSMEVerification = async (msmeNumber) => {
    try {
      setIsLoading(true);
      setValidatingField('msmeNumber');
      setValidationErrors(prev => ({ ...prev, msmeNumber: null }));
      setMsmeInfo(null);
      setFullMsmeInfo(null);

      const response = await kycService.verifyDocument(5, msmeNumber);

      console.log('MSME verification response:', response);

      // Check if response has the expected structure
      if (response?.response_code === "100" && response?.result) {
        setMsmeInfo(response?.result);
        setFullMsmeInfo(response);
        setValidationErrors(prev => ({ ...prev, msmeNumber: null }));
      } else if (response?.response_code === "107") {
        // Handle duplicate transaction
        setValidationErrors(prev => ({
          ...prev,
          msmeNumber: "Duplicate transaction detected. Please try again later."
        }));
        setMsmeInfo(null);
        setFullMsmeInfo(null);
      } else {
        // Handle other failed verification
        const errorMessage = response?.response_message ||
                           response?.metadata?.reason_message ||
                           "Invalid MSME number";
        setValidationErrors(prev => ({
          ...prev,
          msmeNumber: errorMessage
        }));
        setMsmeInfo(null);
        setFullMsmeInfo(null);
      }
    } catch (error) {
      console.log('Error validating MSME number:', error);
      const errMessage = error.message === "Network error or timeout. Please check your connection." ||
                        error.message === "Request Timeout. Unable to process request. API: VerifyDocumentByGovernmentId ";
      setValidationErrors(prev => ({
        ...prev,
        msmeNumber: errMessage ? "Invalid Udyog Aadhar Number or Network timeout Please check and try again." : (error.message || "Error validating MSME number")
      }));
      setMsmeInfo(null);
      setFullMsmeInfo(null);
    } finally {
      setIsLoading(false);
      setValidatingField(null);
    }
  };

  const initialValues = {
    msmeNumber: existingData.msmeNumber || '',
  };

  const handleSubmit = (values) => {
    // Save complete data to context
    const completeData = {
      ...values,
      verifiedInfo: msmeInfo,
      fullVerifiedInfo: fullMsmeInfo
    };

    updateSection('msme', completeData);

    // Mark step as complete if MSME number is provided and verified
    if (values.msmeNumber && msmeInfo) {
      markStepComplete(3);
    } else if (!values.msmeNumber) {
      // MSME is optional, so mark as complete even without number
      markStepComplete(3);
    } else {
      markStepIncomplete(3);
    }

    onComplete({
      msmeRegistration: completeData
    });
  };

  const handleSkip = () => {
    const completeData = {
      msmeNumber: '',
      verifiedInfo: null,
      fullVerifiedInfo: null,
      skipped: true,
    };

    updateSection('msme', completeData);
    markStepComplete(3);

    if (onComplete) {
      onComplete({ msmeRegistration: completeData });
    }
  };

  return (
    <div className="meme-step-formstep-form">
      <h2>{title}</h2>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        innerRef={ref}
        enableReinitialize={true}
      >
        {({ values, isSubmitting, touched, errors, setFieldValue, submitForm }) => {
          useImperativeHandle(ref, () => ({ submitForm }));
          return (
            <Form>
              <div className="msme-form-field">
                <label htmlFor="msmeNumber" style={{ margin: '12px 0' }}>Udyog Aadhaar Number</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <span style={{
                    padding: '11px 12px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px 0 0 4px',
                    color: '#6c757d',
                    fontWeight: '500',
                    fontSize: '16px',
                  }}>
                    UDYAM-
                  </span>
                  <Field
                    type="text"
                    name="msmeNumber"
                    id="msmeNumber"
                    className={`meme-form-input ${touched.msmeNumber && (errors.msmeNumber || validationErrors.msmeNumber) ? 'error' : ''}`}
                    style={{
                      borderRadius: '0 0.375rem 0.375rem 0',
                      marginLeft: 0,
                      flex: 1,
                      borderRight: !isMobile && 'none',
                      height: '50px'
                    }}
                    placeholder="TS-99-9999999"
                    disabled={isLoading}
                    onChange={(e) => {
                      const value = e.target.value;
                      const upperCaseValue = convertLettersToUppercase(value);
                      setFieldValue('msmeNumber', upperCaseValue);
                    }}
                  />
                  <button
                    type="button"
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                      background: isLoading || !/^[A-Z]{2}-\d{2}-\d{7}$/.test(values.msmeNumber) || errors.msmeNumber ? '#6c757d' : '#1b62ab',
                      color: '#fff',
                      border: `1px solid ${isLoading || !/^[A-Z]{2}-\d{2}-\d{7}$/.test(values.msmeNumber) || errors.msmeNumber ? '#6c757d' : '#1b62ab'}`,
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      padding: '0 16px',
                      cursor: (isLoading || !/^[A-Z]{2}-\d{2}-\d{7}$/.test(values.msmeNumber) || errors.msmeNumber) ? 'not-allowed' : 'pointer',
                      opacity: (isLoading || !/^[A-Z]{2}-\d{2}-\d{7}$/.test(values.msmeNumber) || errors.msmeNumber) ? 0.6 : 1,
                      height: '50px',
                      minWidth: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '0px 0.375rem 0.375rem 0px',
                      gap: '8px'
                    }}
                    disabled={isLoading || !/^[A-Z]{2}-\d{2}-\d{7}$/.test(values.msmeNumber) || errors.msmeNumber}
                    onClick={() => handleMSMEVerification(`UDYAM-${values.msmeNumber}`)}
                  >
                    {isLoading && validatingField === 'msmeNumber' ? (
                      <>
                        <div className="loading-spinner" style={{ width: '16px', height: '16px', border: '2px solid #f3f3f3', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                        Validating...
                      </>
                    ) : (
                      'Validate'
                    )}
                  </button>
                </div>
                <ErrorMessage name="msmeNumber" component="div" className="meme-error-message" />
                <div className="meme-form-hint" style={{ marginTop: '16px' }}>
                  Format: [State Code]-[Year]-[7-digit number]
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '16px' }}>
                  <div style={{ marginRight: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <label htmlFor="skip" style={{ display: 'inline-flex', alignItems: 'center', height: '40px' }}>Not applicable to you?</label>
                  <button
                    type="button"
                    aria-label="Skip Udyog Aadhaar step"
                    onClick={handleSkip}
                    onMouseEnter={e => { e.currentTarget.style.background = '#1b62ab'; e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.color = '#1b62ab'; }}
                    style={{
                      background: '#ffffff',
                      color: '#1b62ab',
                      border: '1.5px solid #1b62ab',
                      borderRadius: '999px',
                      padding: '10px 18px',
                      fontWeight: 600,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: '0 1px 3px rgba(27, 98, 171, 0.15)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    ⏭️ Skip this step
                  </button>
                  </div>
                </div>
              </div>

              {/* Verification Result */}
              {msmeInfo?.name_of_enterprise && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '0.5rem',
                  color: '#28a745'
                }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '0.5rem' }}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <circle cx="10" cy="10" r="10" fill="#7ED957"/>
                      <path d="M6 10.5L9 13.5L14 8.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <span>MSME Verified - {msmeInfo.name_of_enterprise}</span>
                </div>
              )}

              {validatingField === 'msmeNumber' && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginTop: '0.5rem',
                  color: '#757d84'
                }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '0.5rem' }}>
                    <svg width="18" height="18" viewBox="0 0 50 50">
                      <circle cx="25" cy="25" r="20" fill="none" stroke="#7ED957" strokeWidth="5" strokeDasharray="31.4 31.4" strokeLinecap="round">
                        <animateTransform attributeName="transform" type="rotate" repeatCount="indefinite" dur="0.8s" from="0 25 25" to="360 25 25"/>
                      </circle>
                    </svg>
                  </span>
                  <span>Verifying MSME details...</span>
                </div>
              )}

              {validationErrors.msmeNumber && (
                <div className="error-message">{validationErrors.msmeNumber}</div>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
});

export default MSMERegistration;